import { ReactNode } from 'react'
import {
    ArrowTopRightOnSquareIcon,
    DocumentTextIcon,
    NewspaperIcon,
    ScaleIcon,
    ShieldCheckIcon,
    UsersIcon
} from '@heroicons/react/24/outline'
import PageMasthead from '../components/PageMasthead'
import { useTitle } from '../components/TitleProvider'

// Brand motion: 200ms on the Arwen easing curve, shared so cards, links and icons
// move as one system. Mirrors the Support page treatment.
const motion = 'transition-colors duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)]'
// Red focus ring, applied everywhere and never removed — the app-wide convention.
const focusRing = 'focus:outline-none focus:ring-2 focus:ring-arwen-500 focus:ring-offset-2'

const social = [
    {
        name: 'Facebook',
        href: 'https://business.facebook.com/ARWEN.SOCIAL',
        icon: () => (
            <svg fill="currentColor" viewBox="0 0 24 24">
                <path
                    fillRule="evenodd"
                    d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                    clipRule="evenodd"
                />
            </svg>
        )
    },
    {
        name: 'Instagram',
        href: 'https://www.instagram.com/arwen.ai/',
        icon: () => (
            <svg fill="currentColor" viewBox="0 0 24 24">
                <path
                    fillRule="evenodd"
                    d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                    clipRule="evenodd"
                />
            </svg>
        )
    },
    {
        name: 'Twitter',
        href: 'https://twitter.com/ArwenAi',
        icon: () => (
            <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
            </svg>
        )
    }
]

interface LinkCardProps {
    /** Decorative glyph shown in the red-wash tile; the card's heading carries the name. */
    icon: ReactNode
    title: string
    description: string
    href: string
}

/**
 * A resource/legal link rendered as a whole-card anchor: red-wash icon tile, an
 * external-link cue top-right, then title and one line of description. The tile and
 * cue are aria-hidden so the accessible name comes from the heading text alone.
 */
function LinkCard({ icon, title, description, href }: LinkCardProps) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className={`group flex flex-col rounded-lg border border-ink-200 bg-white p-7 shadow-sm ${motion} hover:border-ink-300 hover:bg-ink-50 ${focusRing}`}
        >
            <div className="flex items-start justify-between">
                <div
                    aria-hidden="true"
                    className="flex h-12 w-12 items-center justify-center rounded-lg bg-arwen-50 text-arwen-700"
                >
                    {icon}
                </div>
                <ArrowTopRightOnSquareIcon
                    aria-hidden="true"
                    className={`h-5 w-5 text-ink-400 ${motion} group-hover:text-ink-600`}
                />
            </div>
            <h3 className="mt-5 text-lg font-medium text-ink-900">{title}</h3>
            <p className="mt-1 text-sm text-ink-500">{description}</p>
        </a>
    )
}

/** UPPERCASE tracked eyebrow that labels each section. */
function SectionLabel({ children }: { children: ReactNode }) {
    return (
        <h2 className="text-xs font-medium uppercase tracking-wider text-ink-500">{children}</h2>
    )
}

function AboutPage() {
    useTitle('About')
    return (
        <div className="min-h-full bg-ink-50">
            <PageMasthead title="About" subtitle="Arwen resources, privacy and terms" />
            <div className="mx-auto w-full max-w-5xl space-y-10 px-4 py-5 sm:px-6 lg:px-8">
                {/* Resources */}
                <section>
                    <SectionLabel>Resources</SectionLabel>
                    <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <LinkCard
                            icon={<UsersIcon className="h-6 w-6" />}
                            title="Meet the team"
                            description="Get to know the people behind Arwen."
                            href="https://www.arwen.ai/about"
                        />
                        <LinkCard
                            icon={<NewspaperIcon className="h-6 w-6" />}
                            title="Blog"
                            description="All our posts, guides and whitepapers."
                            href="https://blog.arwen.ai/arwen-blog"
                        />
                    </div>

                    {/* Socials bar — mirrors the Support page's slim self-serve bar. */}
                    <div className="mt-6 flex flex-col gap-3 rounded-lg border border-ink-200 bg-white px-7 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm text-ink-700">Follow Arwen on social media.</p>
                        <div className="flex items-center gap-1">
                            {social.map((item) => (
                                <a
                                    key={item.name}
                                    href={item.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={`flex h-9 w-9 items-center justify-center rounded-md text-ink-400 ${motion} hover:bg-ink-100 hover:text-arwen-600 ${focusRing}`}
                                >
                                    <span className="sr-only">{item.name}</span>
                                    <span aria-hidden="true" className="h-5 w-5">
                                        <item.icon />
                                    </span>
                                </a>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Legal */}
                <section>
                    <SectionLabel>Legal</SectionLabel>
                    <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        <LinkCard
                            icon={<ShieldCheckIcon className="h-6 w-6" />}
                            title="Privacy policy"
                            description="How we collect, use and protect data."
                            href="https://arwen.ai/privacy-policy/"
                        />
                        <LinkCard
                            icon={<DocumentTextIcon className="h-6 w-6" />}
                            title="Terms and conditions"
                            description="The terms governing use of Arwen."
                            href="https://arwen.ai/terms-and-conditions/"
                        />
                        <LinkCard
                            icon={<ScaleIcon className="h-6 w-6" />}
                            title="Arwen EULA"
                            description="Our end-user licence agreement."
                            href="https://arwen.ai/eula/"
                        />
                    </div>
                </section>

                <p className="text-center text-sm text-ink-400">
                    &copy; 2026 Arwen Ltd. All rights reserved.
                </p>
            </div>
        </div>
    )
}

export default AboutPage
